import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { api } from '../App';
import { BookOpen, ArrowLeft, ArrowRight, CheckCircle, User, Loader2 } from 'lucide-react';

export default function ObserverAssessment({ user }) {
  const { assessmentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [studentName, setStudentName] = useState('');
  const [isObserver, setIsObserver] = useState(false);

  const targetUserId = searchParams.get('target');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get student info
        if (targetUserId) {
          const infoRes = await api.get(`/students/${targetUserId}/assessment-info`);
          setStudentName(infoRes.data.student_name);
          setIsObserver(infoRes.data.is_young_student);
        }

        // Get questions
        const params = new URLSearchParams();
        if (targetUserId) {
          params.append('target_user_id', targetUserId);
          params.append('assessment_type', 'teacher_about_student');
        }
        const qRes = await api.get(`/questions?${params.toString()}`);
        setQuestions(qRes.data.questions || []);
      } catch (error) {
        toast.error('Sorular yuklenemedi');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [targetUserId]);

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
    // Auto-advance to next question
    if (currentIndex < questions.length - 1) {
      setTimeout(() => setCurrentIndex(currentIndex + 1), 300);
    }
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error('Lutfen tum sorulari cevaplayin');
      return;
    }
    setSubmitting(true);
    try {
      const answerList = Object.entries(answers).map(([qId, value]) => ({
        question_id: parseInt(qId),
        answer_value: value
      }));
      const response = await api.post('/assessments/submit', {
        assessment_id: assessmentId,
        answers: answerList
      });
      toast.success('Envanter basariyla tamamlandi!');
      if (response.data.report_id) {
        navigate(`/report/${response.data.report_id}`);
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Envanter gonderilemedi');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = (Object.keys(answers).length / questions.length) * 100;
  const scaleOptions = [
    { value: 1, label: 'Hic Katilmiyorum', color: 'bg-red-100 border-red-300 text-red-700 hover:bg-red-200' },
    { value: 2, label: 'Katilmiyorum', color: 'bg-orange-100 border-orange-300 text-orange-700 hover:bg-orange-200' },
    { value: 3, label: 'Kararsizim', color: 'bg-yellow-100 border-yellow-300 text-yellow-700 hover:bg-yellow-200' },
    { value: 4, label: 'Katiliyorum', color: 'bg-emerald-100 border-emerald-300 text-emerald-700 hover:bg-emerald-200' },
    { value: 5, label: 'Tamamen Katiliyorum', color: 'bg-green-100 border-green-300 text-green-700 hover:bg-green-200' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-800">Mizac Envanteri</h1>
              {studentName && (
                <p className="text-sm text-slate-600 flex items-center gap-1">
                  <User className="w-3 h-3" /> {studentName} icin
                  {isObserver && <Badge variant="outline" className="ml-1 text-xs bg-amber-50 text-amber-700">Gozlemci Formu</Badge>}
                </p>
              )}
            </div>
          </div>
          <Badge variant="outline" className="bg-blue-50">
            {Object.keys(answers).length}/{questions.length}
          </Badge>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-slate-200 rounded-full h-2 mb-6">
          <div className="bg-gradient-to-r from-blue-600 to-emerald-600 h-2 rounded-full transition-all duration-500" style={{ width: `${progress}%` }}></div>
        </div>

        {/* Question Card */}
        {currentQuestion && (
          <Card className="shadow-xl border-none mb-6" data-testid="question-card">
            <CardHeader className="text-center">
              <CardDescription>Soru {currentIndex + 1} / {questions.length}</CardDescription>
              <CardTitle className="text-xl leading-relaxed mt-2" data-testid="question-text">
                {isObserver && studentName ? `"${studentName}" icin: ` : ''}
                {currentQuestion.text}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {scaleOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(currentQuestion.id, option.value)}
                    data-testid={`answer-${option.value}`}
                    className={`w-full p-4 rounded-xl border-2 text-left font-medium transition-all ${
                      answers[currentQuestion.id] === option.value
                        ? 'ring-2 ring-blue-500 scale-[1.02] ' + option.color
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 border-current">
                        {option.value}
                      </span>
                      <span>{option.label}</span>
                      {answers[currentQuestion.id] === option.value && (
                        <CheckCircle className="w-5 h-5 ml-auto" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button variant="outline" onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))} disabled={currentIndex === 0} data-testid="prev-question">
            <ArrowLeft className="w-4 h-4 mr-2" /> Onceki
          </Button>

          {currentIndex < questions.length - 1 ? (
            <Button onClick={() => setCurrentIndex(currentIndex + 1)} className="bg-gradient-to-r from-blue-600 to-emerald-600" data-testid="next-question">
              Sonraki <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={Object.keys(answers).length < questions.length || submitting}
              className="bg-gradient-to-r from-emerald-600 to-blue-600" data-testid="submit-assessment">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gonderiliyor...</> : <><CheckCircle className="w-4 h-4 mr-2" /> Tamamla</>}
            </Button>
          )}
        </div>

        {/* Question dots */}
        <div className="flex flex-wrap justify-center gap-2 mt-6">
          {questions.map((q, i) => (
            <button key={q.id} onClick={() => setCurrentIndex(i)}
              className={`w-8 h-8 rounded-full text-xs font-bold transition-all ${
                i === currentIndex ? 'bg-blue-600 text-white scale-110' :
                answers[q.id] ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'
              }`}>
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
